/**
 * RegIdChip — monospaced, copy-to-clipboard pill for a child's registration number.
 *
 * Used on every child page (Profile / Vaccination / Growth) so the reg ID is
 * always presented the same way: prominent, scannable, and one click away
 * from being copied to the clipboard.
 *
 *   <RegIdChip value={profile.childNo} />
 *   <RegIdChip value={profile.childNo} label="Reg No" />
 *
 * Falls back to a plain dash when the value is missing.
 */
import React, { useState, useCallback } from 'react';
import Tooltip from '@mui/material/Tooltip';
import { FiCopy, FiCheck } from 'react-icons/fi';
import './RegIdChip.css';

export function RegIdChip({ value, label = 'Reg ID', size = 'medium', className = '' }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = useCallback(
        async (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!value) return;
            try {
                if (navigator?.clipboard?.writeText) {
                    await navigator.clipboard.writeText(String(value));
                } else {
                    const ta = document.createElement('textarea');
                    ta.value = String(value);
                    ta.style.position = 'fixed';
                    ta.style.opacity = '0';
                    document.body.appendChild(ta);
                    ta.select();
                    document.execCommand('copy');
                    document.body.removeChild(ta);
                }
                setCopied(true);
                setTimeout(() => setCopied(false), 1400);
            } catch {
                /* noop — clipboard not available; ignore */
            }
        },
        [value],
    );

    if (!value) {
        return (
            <span
                className={`pt-reg-chip pt-reg-chip--missing ${className}`}
                data-size={size}
                aria-label={`${label} not available`}
            >
                <span className="pt-reg-chip__label">{label}</span>
                <span className="pt-reg-chip__value">—</span>
            </span>
        );
    }

    return (
        <Tooltip title={copied ? 'Copied!' : 'Click to copy'} placement="top" arrow>
            <button
                type="button"
                onClick={handleCopy}
                className={`pt-reg-chip ${copied ? 'is-copied' : ''} ${className}`}
                data-size={size}
                aria-label={`${label}: ${value}. Click to copy.`}
            >
                <span className="pt-reg-chip__label">{label}</span>
                <span className="pt-reg-chip__value">{value}</span>
                <span className="pt-reg-chip__icon" aria-hidden="true">
                    {copied ? <FiCheck size={12} /> : <FiCopy size={12} />}
                </span>
            </button>
        </Tooltip>
    );
}

export default RegIdChip;
